import { Box, Flex, IconButton } from "@chakra-ui/react";
import { userFullName } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types/dto";
import { useNavigate } from "react-router";

import { Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

type SavedProfile = DTO.SavedProfile;
type SavedProfileType = DTO.TAAccountType;

export type SavedProfileCardProps = {
  profile: SavedProfile;
};

const labelByType: Record<SavedProfileType, string> = {
  INDIVIDUAL: "Individual Account",
  JOINT: "Joint Account",
  TRUST: "Trust Account",
  ENTITY: "Entity Account",
};

const iconByType: Record<SavedProfileType, JSX.Element> = {
  INDIVIDUAL: <Icon.User size="24px" />,
  JOINT: <Icon.Users size="24px" />,
  TRUST: <Icon.FileText size="24px" />,
  ENTITY: <Icon.Briefcase size="24px" />,
};

export const SavedProfileCard = ({ profile }: SavedProfileCardProps) => {
  const navigate = useNavigate();

  const accountType = profile.account_type;
  const displayName = getSavedProfileDisplayName(profile);

  return (
    <Flex
      align="center"
      justify="space-between"
      bg="white"
      borderRadius="lg"
      p="4"
      minH="84px"
      boxShadow="0 1px 2px rgba(0, 0, 0, 0.2)"
      border="1px solid"
      borderColor="gray.100">
      <Box minW="0">
        <Text m="0" mb="2" fontSize="16px" lineHeight="19.2px" fontWeight="700" noOfLines={1}>
          {displayName}
        </Text>

        <Flex align="center" gap="2.5" color="#657797">
          {accountType ? iconByType[accountType] : <Icon.User size="24px" />}

          <Text m="0" fontSize="16px" lineHeight="24px" fontWeight="400" color="#657797" noOfLines={1}>
            {accountType ? labelByType[accountType] : "Investment Account"}
          </Text>
        </Flex>
      </Box>

      <IconButton
        aria-label="Edit saved profile"
        icon={<Icon.Edit size="28px" />}
        variant="ghost"
        color="#2F55D4"
        flexShrink="0"
        onClick={() => navigate(`/vault/profile/saved-profiles/${profile.id}`)}
      />
    </Flex>
  );
};

const getSavedProfileDisplayName = (profile: SavedProfile) => {
  if (profile.company) return profile.company;

  const fullName = userFullName(profile, { middle: "none" });

  return profile?.first_name ? fullName : "Saved Profile";
};
